import React, { useEffect, useState } from "react";
import { object, number, string, array } from "prop-types";
import { Link, WithLazy } from "@quintype/components";
import { useSelector } from "react-redux";
import { get } from "lodash";

import { getStoryUrl } from "../../../../arrow/utils/utils";
import { Span, Carousel } from "../../../atoms";
import { HeroImage } from "../../../../arrow";
import { STATIC_KEYWORDS } from "../../../../constants";
import { getCategoryAndSubCategory, getTemplateName, useTrackWidgetImpression } from "../../../../ga-utils";
import { getFilteredMagazineCollection, getMagazineCollectionDetails } from "../../../../../api/utils";

import "./RecommendedStories.m.css";

const StoryCard = ({ story, position, parentStory, config }) => {
  const trackRecommendedClick = () => {
    if (typeof window !== "undefined" && window.dataLayer) {
      const pageType = "story-page";
      const { category, subCategory, contentId } = getCategoryAndSubCategory(
        window.location,
        pageType,
        config,
        parentStory
      );
      window.dataLayer.push({
        event: "dl_story_interactions",
        event_action: "recommended_story_click",
        link_position: position,
        category,
        sub_category: subCategory,
        content_id: contentId,
        template_name: getTemplateName(parentStory?.["story-template"] || pageType),
        platform: window.innerWidth >= 768 ? "desktop" : "mobile",
      });
    }
  };

  return (
    <div onClick={trackRecommendedClick} styleName="story-card-wrapper">
      <div styleName="hero-image-wrapper">
        <WithLazy margin="150px">
          {() => <HeroImage story={story} aspectRatio={[[16, 9]]} isRecommended={true} />}
        </WithLazy>
      </div>
      <Link href={getStoryUrl(story, `/${story?.slug}`)} disableAjaxLinks={true}>
        <Span customStyleName="recommended-story-span">
          {story?.headline}
        </Span>
      </Link>
    </div>
  );
};
StoryCard.propTypes = {
  story: object,
  position: number,
  parentStory: object,
};

async function magazineData(storyId) {
  try {
    const { issueSlug, magazineSlug } = await getFilteredMagazineCollection(storyId);
    const result = await getMagazineCollectionDetails(issueSlug, magazineSlug);
    return result;
  } catch (error) {
    console.error("Error fetching magazine data:", error);
    return null;
  }
}

const RecommendedStories = ({ recommendedStories, qtConfig = {} }) => {
  const { RECOMMENDED_STORIES, MORE_STORIES_FROM_ISSUE } = STATIC_KEYWORDS;
  const qtState = useSelector((state) => get(state, ["qt"], {}));
  const story = get(qtState, ["data", "story"], {});
  const isMagazine = story["story-template"] === "magazine";
  const isBrowser = typeof window !== "undefined";
  const [magazineDetails, setMagazineDetails] = useState(null);

  let finalMagazineStories = {};

  useEffect(() => {
    if (isMagazine) {
      magazineData(story["story-content-id"]).then((res) => {
        setMagazineDetails(res);
        console.log("Fetched magazineDetails:", res);
      });
    }
  }, [isMagazine, story["story-content-id"]]);

  if (magazineDetails?.data) {
    const { items } = magazineDetails.data;
    const topFive = items?.slice(0, 5) || [];
    const filtered = topFive.filter((item) => item?.story?.slug !== story?.slug);
    const slicedItems = filtered.slice(0, 4);

    finalMagazineStories = slicedItems.map(({ story }) => ({
      id: story?.id,
      headline: story?.headline,
      "hero-image-metadata": story?.["hero-image-metadata"],
      "hero-image-alt-text": story?.["hero-image-alt-text"],
      "hero-image-s3-key": story?.["hero-image-s3-key"],
      slug: story?.slug,
      "story-template": story?.["story-template"],
      url: `${story?.slug}`,
    }));
  }
  const finalStoryLength = isMagazine ? finalMagazineStories?.length : recommendedStories?.length;
  const shouldTrack = isBrowser && story && finalStoryLength > 0;
  const widgetTitle = isMagazine && finalMagazineStories?.length > 0 ? MORE_STORIES_FROM_ISSUE : RECOMMENDED_STORIES;

  // Use the custom hook for tracking widget impressions
  const containerRef = useTrackWidgetImpression(
    {
      sectionName: "Recommended Stories",
      pageType: "story-page",
      location: isBrowser ? window.location : null,
      config: qtConfig,
      story: story,
      threshold: 0.2,
    },
    shouldTrack ? undefined : { disabled: true }
  );

  return (
    <>
      {finalStoryLength > 0 && (
        <div className="recommended-div" styleName="container-div" ref={containerRef}>
          <div styleName="recommended-stories-heading">{RECOMMENDED_STORIES}</div>
          <Carousel
            itemWidth={182}
            gap={12}
            showDots={recommendedStories?.length > 2}
            // showButtons={true}
            maxItemWidth={182}
            itemMobileWidth={182}
          >
            {recommendedStories?.map((recStory, index) => {
              const storyUrl = `${recStory?.url}?utm_medium=related-stories`;
              return (
                <StoryCard
                  story={{ ...recStory, url: storyUrl }}
                  key={recStory?.id}
                  position={index + 1}
                  parentStory={story}
                  config={qtConfig}
                />
              );
            })}
          </Carousel>
        </div>
      )}
    </>
  );
};

RecommendedStories.propTypes = {
  recommendedStories: array,
  qtConfig: object,
  sectionId: number,
  storyId: string,
};

export default RecommendedStories;
